/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Divider,
  Flex,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Text,
} from '@tonic-one/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { PolicyApi } from '@Apis';
import {
  CheckboxTitle,
  FormLabel,
  TitleLabel,
} from 'src/components/Common/FormComponent';
import { Info } from 'src/components/Common/Info';

import {
  storeSelector,
  templateSelector,
  usePolicyContext,
} from '@Policy/store';
import { useRulesStyle } from './style';
import TimeOfClickModal from './TimeOfClickModal';
import { useWhyDidYouUpdate } from 'src/utils/hooks';
import ApplyBound from '../../Components/ApplyBound';
import { ExchangeOnlinePolicyItem } from '@Policy/utils/types';
import RetroScanEnableModal from '@Policy/AdvancedThreatProtection/PolicyTemplate/Components/RetroScanEnableModal';
import { PolicyModeEnum, PolicyTypeEnum } from '@Policy/utils/constants';

export const WtpApplyBound = () => {
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state from store
  const wtpApplyBound = policySelector(
    (s) => s.tmWebthreatprotections.wtpApplyBound
  );
  // get action from store
  const setWtpState = contextSelector((s) => s.setWtpState);
  useWhyDidYouUpdate('WtpApplyBound', { wtpApplyBound });
  return (
    <ApplyBound
      applyBoundValue={wtpApplyBound}
      setApplyBound={setWtpState}
      applyBoundKey="wtpApplyBound"
    />
  );
};
export const WtpStatus = () => {
  const { t } = useTranslation();

  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state from store
  const wtpEnabled = policySelector((s) => s.tmWebthreatprotections.wtpEnabled);
  // get action from store
  const setWtpState = contextSelector((s) => s.setWtpState);
  const setDdaUrlEnabled = contextSelector((s) => s.setDdaUrlEnabled);
  // local state
  const handleWtpEnabledChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setWtpState({
        wtpEnabled: e.target.checked ? 1 : 0,
      });
      if (e.target.checked === false) {
        setDdaUrlEnabled(0);
      }
    },
    []
  );
  useWhyDidYouUpdate('WtpStatus', { wtpEnabled });
  return (
    <Flex alignItems="center">
      <Switch
        mr="1x"
        name="wtpEnabled"
        value={Number(!!wtpEnabled)}
        checked={!!wtpEnabled}
        onChange={handleWtpEnabledChange}
      />
      <TitleLabel title={t('policy.webReputation')} mr="2x" />
    </Flex>
  );
};
export const WtpDetectionLevel = () => {
  const { t } = useTranslation();

  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state from store
  const wtpSecurityLevel = policySelector(
    (s) => s.tmWebthreatprotections.wtpSecurityLevel
  );
  // get action from store
  const setWtpState = contextSelector((s) => s.setWtpState);
  // local state
  const handleWtpSecurityLevelChange = React.useCallback(
    (value: number) => setWtpState({ wtpSecurityLevel: Number(value) }),
    []
  );
  useWhyDidYouUpdate('WtpDetectionLevel', { wtpSecurityLevel });
  return (
    <Flex mt="23px" alignItems="flex-start">
      <FormLabel label={t('policy.webSecurityLevel.title')} width="200px" />
      <RadioGroup
        value={wtpSecurityLevel}
        name="wtpSecurityLevel"
        onChange={handleWtpSecurityLevelChange}
      >
        <Stack spacing="1x" shouldWrapChildren>
          <Radio value={0}>
            <CheckboxTitle
              title={t('policy.detectionLevel.high')}
              subTitle={t('policy.webDetectionLevel.highDesc')}
            />
          </Radio>
          <Radio value={1}>
            <CheckboxTitle
              title={t('policy.detectionLevel.medium')}
              subTitle={t('policy.webDetectionLevel.mediumDesc')}
            />
          </Radio>
          <Radio value={2}>
            <CheckboxTitle
              title={t('policy.detectionLevel.low')}
              subTitle={t('policy.webDetectionLevel.lowDesc')}
            />
          </Radio>
        </Stack>
      </RadioGroup>
    </Flex>
  );
};
export const WtpMessageAttachment = () => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state from store
  const wtpExtractAttachmentUrl = policySelector(
    (s) => s.tmWebthreatprotections.wtpExtractAttachmentUrl
  );
  // get action from store
  const setWtpState = contextSelector((s) => s.setWtpState);
  // local state
  const { subTitleColor } = useRulesStyle();
  const handleWtpExtractAttachmentUrlChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setWtpState({ wtpExtractAttachmentUrl: e.target.checked ? 1 : 0 });
    },
    []
  );
  useWhyDidYouUpdate('WtpMessageAttachment', { wtpExtractAttachmentUrl });
  return (
    <Flex mt="24px" direction="column">
      <Flex mb="12px" direction="row" alignItems="center">
        <Switch
          name="wtpExtractAttachmentUrl"
          mr="1x"
          checked={!!wtpExtractAttachmentUrl}
          value={wtpExtractAttachmentUrl}
          onChange={handleWtpExtractAttachmentUrlChange}
          size="sm"
        />
        <Text fontSize="16px" lineHeight="22px">
          {t('policy.detectionSettings.messageAttachment')}
        </Text>
      </Flex>
      <Text color={subTitleColor}>
        {t('policy.detectionSettings.messageAttachment.desc')}
      </Text>
    </Flex>
  );
};
export const WtpDynamicUrl = () => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state from store

  const wtpDynamicScanEnabled = policySelector(
    (s) => s.tmWebthreatprotections.wtpDynamicScanEnabled
  );
  // get action from store
  const setWtpState = contextSelector((s) => s.setWtpState);
  // local state
  const { subTitleColor } = useRulesStyle();
  const handleWtpDynamicScanEnabledChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setWtpState({ wtpDynamicScanEnabled: e.target.checked ? 1 : 0 });
    },
    []
  );
  useWhyDidYouUpdate('WtpDynamicUrl', { wtpDynamicScanEnabled });
  return (
    <Flex mt="24px" direction="column">
      <Flex mb="12px" alignItems="center">
        <Switch
          name="wtpDynamicScanEnabled"
          mr="1x"
          checked={!!wtpDynamicScanEnabled}
          value={wtpDynamicScanEnabled}
          onChange={handleWtpDynamicScanEnabledChange}
          size="sm"
        />
        <Text fontSize="16px" lineHeight="22px">
          {t('policy.detectionSettings.dynamicURL')}
        </Text>
      </Flex>
      <Text color={subTitleColor}>
        {t('policy.detectionSettings.dynamicURL.desc')}
      </Text>
    </Flex>
  );
};
export const WtpRetroScan = (props: {
  handleRetroScan: () => void;
  handleRetroScanModalClose: () => void;
}) => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state from store

  const wtpRetroScanEnabled = policySelector(
    (s) => s.tmWebthreatprotections.wtpRetroScanEnabled
  );

  // get action from store
  const setWtpState = contextSelector((s) => s.setWtpState);
  // local state
  const { iconColor, subTitleColor } = useRulesStyle();

  const handleWtpRetroScanEnabledChange = (e: any) => {
    props.handleRetroScan();
    setWtpState({ wtpRetroScanEnabled: e.target.checked ? 1 : 0 });
  };
  useWhyDidYouUpdate('WtpRetroScan', { wtpRetroScanEnabled });
  return (
    <Flex mt="24px" direction="column">
      <Flex mb="12px" alignItems="center">
        <Switch
          name="wtpRetroScanEnabled"
          mr="1x"
          checked={!!wtpRetroScanEnabled}
          value={wtpRetroScanEnabled}
          onChange={handleWtpRetroScanEnabledChange}
          size="sm"
        />
        <Text fontSize="16px" lineHeight="22px">
          {t('policy.detectionSettings.retroScanTitle')}
        </Text>
      </Flex>
      <Flex alignItems="center">
        <Text color={subTitleColor}>
          {t('policy.detectionSettings.retroScanTitleWeb.desc')}
        </Text>
        <Info
          iconStyle={{ color: iconColor, ml: '2x' }}
          content={t('policy.detectionSettings.retroScanTitleWeb.desc.info')}
        />
      </Flex>
    </Flex>
  );
};
export const WtpTimeOfClick = () => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state from store
  const wtpTimeOfClickEnabled = policySelector(
    (s) => s.tmWebthreatprotections.wtpTimeOfClickEnabled
  );
  const wtpTimeOfClickApplyType = policySelector(
    (s) => s.tmWebthreatprotections.wtpTimeOfClickApplyType
  );
  const policyMode = policySelector((s) => s.policyMode);
  // get action from store
  const setWtpState = contextSelector((s) => s.setWtpState);
  // local state
  const { iconColor, radioTextColor } = useRulesStyle();
  const [isVisible, setIsVisible] = React.useState<boolean>(false);
  const handleTimeOfClickClose = React.useCallback(() => {
    setWtpState({ wtpTimeOfClickEnabled: 0 });
    setIsVisible(false);
  }, []);
  const handleTimeOfClickSave = React.useCallback(async () => {
    try {
      await PolicyApi.registerTimeOfClick();
    } catch (error) {
      setWtpState({ wtpTimeOfClickEnabled: 0 });
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
      return;
    } finally {
      setIsVisible(false);
    }
    setWtpState({ wtpTimeOfClickEnabled: 1 });
  }, []);
  const handleWtpTimeOfClickEnabledChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.currentTarget.value === '0') {
        setIsVisible(true);
      }
      setWtpState({ wtpTimeOfClickEnabled: e.target.checked ? 1 : 0 });
    },
    []
  );
  const handleWtpTimeOfClickApplyTypeChange = React.useCallback(
    (value: number) => {
      setWtpState({ wtpTimeOfClickApplyType: Number(value) });
    },
    []
  );
  useWhyDidYouUpdate('WtpTimeOfClick', {
    wtpTimeOfClickEnabled,
    wtpTimeOfClickApplyType,
  });
  return (
    <Flex mt="24px" direction="column">
      <Flex mb="12px" alignItems="center">
        <Switch
          name="wtpTimeOfClickEnabled"
          mr="1x"
          checked={!!wtpTimeOfClickEnabled}
          value={wtpTimeOfClickEnabled}
          onChange={handleWtpTimeOfClickEnabledChange}
          size="sm"
          disabled={
            [
              PolicyModeEnum.DefaultMonitorPolicy,
              PolicyModeEnum.DefaultXDRMonitorPolicy,
            ].includes(policyMode) && !wtpTimeOfClickEnabled
          }
        />
        <TimeOfClickModal
          isVisible={isVisible}
          onClose={handleTimeOfClickClose}
          // config={wtpTimeOfClickEnabled}
          saveConfig={handleTimeOfClickSave}
        />
        <Text fontSize="16px" lineHeight="22px">
          {t('policy.detectionSettings.timeOfClickProtection')}
        </Text>
        <Info
          iconStyle={{ color: iconColor, ml: '2x' }}
          content={t('policy.detectionSettings.timeOfClickProtection.desc')}
        />
      </Flex>
      <RadioGroup
        value={wtpTimeOfClickApplyType}
        onChange={handleWtpTimeOfClickApplyTypeChange}
        disabled={!wtpTimeOfClickEnabled}
      >
        <Stack spacing="3x" shouldWrapChildren mt="6px">
          <Radio value={0}>
            <Text lineHeight="20px" color={radioTextColor}>
              {t('policy.detectionSettings.timeOfClickProtection.beenTested')}
            </Text>
          </Radio>
          <Radio value={1} color={radioTextColor}>
            {t('policy.detectionSettings.timeOfClickProtection.markedByWeb')}
          </Radio>
          <Radio value={2} color={radioTextColor}>
            {t('policy.detectionSettings.timeOfClickProtection.all')}
          </Radio>
        </Stack>
      </RadioGroup>
    </Flex>
  );
};
export const WtpDetectionTequeniques = (props: {
  messageAttachmentShow: boolean;
  dynamicUrlShow: boolean;
  retroScanShow: boolean;
  timeOfClickShow: boolean;
  phishingURLShow: boolean;
  handleRetroScan: () => void;
  handleRetroScanModalClose: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <>
      <TitleLabel title={t('policy.detectionSettings.title')} />
      {props.messageAttachmentShow ? <WtpMessageAttachment /> : null}
      {props.dynamicUrlShow ? <WtpDynamicUrl /> : null}
      {props.phishingURLShow ? <WtpPhishingUrl /> : null}
      {props.retroScanShow ? (
        <WtpRetroScan
          handleRetroScan={props.handleRetroScan}
          handleRetroScanModalClose={props.handleRetroScanModalClose}
        />
      ) : null}
      {props.timeOfClickShow ? <WtpTimeOfClick /> : null}
    </>
  );
};
export const WtpPhishingUrl = () => {
  const { t } = useTranslation();
  // get store from context
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state from store

  const wtpUrlcvEnabled = policySelector(
    (s) => s.tmWebthreatprotections.wtpUrlcvEnabled
  );
  // get action from store
  const setWtpState = contextSelector((s) => s.setWtpState);
  // local state
  const { subTitleColor } = useRulesStyle();
  const { iconColor } = useRulesStyle();
  const handleWtpUrlcvEnabledChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setWtpState({ wtpUrlcvEnabled: e.target.checked ? 1 : 0 });
    },
    []
  );
  useWhyDidYouUpdate('wtpUrlcvEnabled', { wtpUrlcvEnabled });
  return (
    <Flex mt="24px" direction="column">
      <Flex mb="12px" alignItems="center">
        <Switch
          name="wtpURLCVEnabled"
          mr="1x"
          checked={!!wtpUrlcvEnabled}
          value={wtpUrlcvEnabled}
          onChange={handleWtpUrlcvEnabledChange}
          size="sm"
        />
        <Text fontSize="16px" lineHeight="22px">
          {t('policy.detectionSettings.phishingURL.title')}
        </Text>
        <Info
          iconStyle={{ color: iconColor, ml: '2x' }}
          content={t('policy.detectionSettings.phishingURL.tips')}
        />
      </Flex>
      <Text color={subTitleColor}>
        {t('policy.detectionSettings.phishingURL.desc')}
      </Text>
    </Flex>
  );
};

const Rules = ({
  applyBoundShow = false,
  retroScanShow = false,
  timeOfClickShow = false,
  messageAttachmentShow = true,
  dynamicUrlShow = true,
  detectionTequeniquesShow = false,
}: {
  applyBoundShow?: boolean;
  retroScanShow?: boolean;
  timeOfClickShow?: boolean;
  messageAttachmentShow?: boolean;
  dynamicUrlShow?: boolean;
  detectionTequeniquesShow: boolean;
}) => {
  const urlCVEnableList = [
    PolicyTypeEnum.Exchange,
    PolicyTypeEnum.ExchangeInline,
    PolicyTypeEnum.Gmail,
    PolicyTypeEnum.GmailInline,
  ];
  const contextStore = usePolicyContext();
  // get store selector
  const contextSelector = storeSelector(contextStore);
  const policySelector = templateSelector(
    contextStore
  )<ExchangeOnlinePolicyItem>;
  // get state from store
  const policyType = policySelector((s) => s.policyType);
  const wtpRetroScanEnabled = policySelector(
    (s) => s.tmWebthreatprotections.wtpRetroScanEnabled
  );
  // get action from store
  const setWtpState = contextSelector((s) => s.setWtpState);
  console.log('render rules');
  const [retroScanModal, setRetroScanModal] = React.useState<boolean>(false);
  const handleRetroScan = () => {
    if (wtpRetroScanEnabled === 0) {
      setRetroScanModal(true);
    }
    return;
  };
  const handleRetroScanModalClose = () => {
    setWtpState({ wtpRetroScanEnabled: wtpRetroScanEnabled === 1 ? 0 : 1 });
    setRetroScanModal(false);
  };

  const onConfirm = () => {
    setRetroScanModal(false);
  };
  return (
    <div>
      <Box ml="1px" mt="16px">
        <WtpStatus />
        <Divider mt="32px" mb="32px" />
        {applyBoundShow ? <WtpApplyBound /> : null}
        <WtpDetectionLevel />
        <Divider mt="32px" mb="32px" />
        {detectionTequeniquesShow ? (
          <WtpDetectionTequeniques
            messageAttachmentShow={messageAttachmentShow}
            dynamicUrlShow={dynamicUrlShow}
            retroScanShow={retroScanShow}
            timeOfClickShow={timeOfClickShow}
            phishingURLShow={urlCVEnableList.includes(policyType)}
            handleRetroScan={handleRetroScan}
            handleRetroScanModalClose={handleRetroScanModalClose}
          />
        ) : null}
        <RetroScanEnableModal
          isVisible={retroScanModal}
          onClose={handleRetroScanModalClose}
          onConfirm={onConfirm}
          filterType="advancedSpamProtection"
        />
      </Box>
    </div>
  );
};
export default React.memo(Rules);
